import * as L from "leaflet";
import 'leaflet-measure-path';
import 'leaflet-measure-path/leaflet-measure-path.css';
import MarksStyling from "./MarksStyling";

import './marks.css';
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useMap} from "../../../providers/MapProvider";
import MapHelper from "../../../helpers/MapHelper";
import _ from "lodash";
import "./MarksPopup.css";
import {useApi} from "../../../providers/ApiProvider";
import ShapesRequests from "../../../requests/ShapesRequests";
import {useNotifications} from "../../../providers/NotificationsProvider";
import * as turf from "@turf/turf";
import MarksNotesDisplay from "./MarksNotesDisplay";
import {createRoot} from "react-dom/client";
import MarksInfoPanel from "../general/ui/MarksInfoPanel";
import ShapeStyling from "../shapes/ShapeStyling";
import {useParams} from "react-router-dom";
import ProjectsRequests from "../../../requests/ProjectsRequests";

export default function Marks({id, data, sortOrder = 0, active = false, visible = true, properties = {}, onActiveChange, onSelfDelete, onMarkChange, onConvertShape, canEdit = false, onBringToBack, onBringToFront}) {
    const mapContext = useMap();
    const notifications = useNotifications();
    const {projectId} = useParams();

    const apiContext = useApi();
    const shapesRequests = new ShapesRequests(apiContext.api);
    const projectsRequests = new ProjectsRequests(apiContext.api);

    const mapPaneName = 'marksPane-'+id;

    const shadowLayer = useRef(null);
    let baseBufferRadiusMeasurementUnits = data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.bufferRadiusMeasurementUnits ? data.geoJSON.properties.bufferRadiusMeasurementUnits : 'm';
    const [bufferRadiusMeasurementUnits, setBufferRadiusMeasurementUnits] = useState(baseBufferRadiusMeasurementUnits);
    const bufferRadiusMeasurementUnitsRef = useRef(bufferRadiusMeasurementUnits);
    let baseBufferRadius = data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.bufferRadius ? data.geoJSON.properties.bufferRadius : 0.1;
    const [bufferRadius, setBufferRadius] = useState(baseBufferRadius);
    const bufferRadiusRef = useRef(bufferRadius);

    let baseUnion = data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.union ? data.geoJSON.properties.union : true;
    const [union, setUnion] = useState(baseUnion);

    let baseNotes = data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.notes ? data.geoJSON.properties.notes : null;
    const [notes, setNotes] = useState(baseNotes);

    let baseCustomColor = data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.color ? data.geoJSON.properties.color : MarksStyling.generateRandomColor();
    const [customColor, setCustomColor] = useState(baseCustomColor);
    const [layer, setLayer] = useState(null);
    const layerRef = useRef(layer);
    const [initComplete, setInitComplete] = useState(false);
    const [drawingNewShape, setDrawingNewShape] = useState(true);

    const [infoPanelMinimized, setInfoPanelMinimized] = useState(false);

    const popupRef = useRef(L.popup({ closeOnClick: false, autoClose: false }));

    const getShapeVisibleFromData = (newData) => {
        if (!newData) {newData = data;}
        let baseVisible = true;
        if (newData.geoJSON && newData.geoJSON.properties && newData.geoJSON.properties.visible === false) {
            baseVisible = false;
        }
        return baseVisible;
    }
    let baseShapeVisible = getShapeVisibleFromData();
    const [shapeVisible, setShapeVisible] = useState(baseShapeVisible);

    const getTitleFromData = () => {
        let baseTitle = 'Shape';
        if (data.type === 'Marker') {
            baseTitle = 'Marker Pin';
        }
        if (data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.title) {
            baseTitle = data.geoJSON.properties.title;
        } else if (data.geoJSON && data.geoJSON && data.geoJSON.geometry && data.geoJSON.geometry.type) {
            baseTitle += ' ' + data.geoJSON.geometry.type;
        }
        return baseTitle;
    }
    let baseTitle = getTitleFromData();
    const [title, setTitle] = useState(baseTitle);

    let basePopupTitle = data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.popupTitle ? data.geoJSON.properties.popupTitle : false;
    const [popupTitle, setPopupTitle] = useState(basePopupTitle);

    let userInfo = JSON.parse(localStorage.getItem('user')), unitsType = 'disable';
    if (userInfo && userInfo.settings && userInfo.settings.units && userInfo.settings.units === 'imperial') {
        unitsType = 'imperial';
    } else if (userInfo && userInfo.settings && userInfo.settings.units && userInfo.settings.units === 'metric') {
        unitsType = 'metric';
    } else {
        unitsType = 'disable';
    }
    let baseDrawingOptions = {... MarksStyling.DRAWING_OPTIONS_POSITIVE};
    if (!baseUnion) {baseDrawingOptions = {... MarksStyling.DRAWING_OPTIONS_NEGATIVE};}
    if (unitsType === 'imperial') {
        baseDrawingOptions = {...baseDrawingOptions, pathOptions: {...baseDrawingOptions.pathOptions, showMeasurements: true, measurementOptions: {imperial: unitsType === 'imperial'}}};
        baseDrawingOptions = {...baseDrawingOptions, hintlineStyle: {...baseDrawingOptions.hintlineStyle, showMeasurements: true, measurementOptions: {imperial: unitsType === 'imperial'}}};
        baseDrawingOptions = {...baseDrawingOptions, templineStyle: {...baseDrawingOptions.templineStyle, showMeasurements: true, measurementOptions: {imperial: unitsType === 'imperial'}}};
    } else if (unitsType === 'metric') {
        baseDrawingOptions = {...baseDrawingOptions, pathOptions: {...baseDrawingOptions.pathOptions, showMeasurements: true, measurementOptions: {metric: unitsType === 'metric'}}};
        baseDrawingOptions = {...baseDrawingOptions, hintlineStyle: {...baseDrawingOptions.hintlineStyle, showMeasurements: true, measurementOptions: {metric: unitsType === 'metric'}}};
        baseDrawingOptions = {...baseDrawingOptions, templineStyle: {...baseDrawingOptions.templineStyle, showMeasurements: true, measurementOptions: {metric: unitsType === 'metric'}}};
    } else if (unitsType === 'disable') {
        baseDrawingOptions = {...baseDrawingOptions, pathOptions: {...baseDrawingOptions.pathOptions, showMeasurements: false}};
        baseDrawingOptions = {...baseDrawingOptions, hintlineStyle: {...baseDrawingOptions.hintlineStyle, showMeasurements: false}};
        baseDrawingOptions = {...baseDrawingOptions, templineStyle: {...baseDrawingOptions.templineStyle, showMeasurements: false}};
    }
    if (customColor) {
        let newPathOptions = MarksStyling.applyCustomColor(baseDrawingOptions.pathOptions, customColor);
        baseDrawingOptions = {...baseDrawingOptions, pathOptions: newPathOptions};
    }
    const [drawingOptions, setDrawingOptions] = useState(baseDrawingOptions);

    const onActiveChangeRef = useRef(onActiveChange);
    const onSelfDeleteRef = useRef(onSelfDelete);


    useEffect(() => {
        onActiveChangeRef.current = onActiveChange;
    }, [onActiveChange]);
    useEffect(() => {
        onSelfDeleteRef.current = onSelfDelete;
    }, [onSelfDelete]);


    useEffect(() => {
        layerRef.current = layer;
        updatePopup();
    }, [layer]);


    useEffect(() => {
        if (mapContext.isInitialized && !initComplete) {
            init();
        }

        return selfDelete;
    }, []);


    useEffect(() => {
        bufferRadiusMeasurementUnitsRef.current = bufferRadiusMeasurementUnits;
        bufferRadiusRef.current = bufferRadius;

        if (mapContext.isInitialized && initComplete && layer && (data.type === MarksStyling.TYPE_BUFFER)) {
            let dryShapeData = _getDryShape(layer);
            createBufferShadowLayer(dryShapeData.geoJSON);
            dryShapeData = _getDryShape(layer);

            onMarkChange(id, union, shapeVisible, {
                bufferRadiusMeasurementUnits: bufferRadiusMeasurementUnits,
                bufferRadius: bufferRadius
            });

            onActiveChangeRef.current(id, true, {
                main: dryShapeData.geoJSON,
                shadow: dryShapeData.shadowGeoJSON
            }, dryShapeData.layerData, true);
        }
    }, [bufferRadius, bufferRadiusMeasurementUnits]);





    useEffect(() => {
        if (layer) {
            if (shapeVisible) {
                layer.addTo(mapContext.map);
                enable();
            } else {
                mapContext.map.removeLayer(layer);
            }

            if (data.type === MarksStyling.TYPE_BUFFER && shadowLayer.current) {
                if (shapeVisible) {
                    shadowLayer.current.addTo(mapContext.map);
                    shadowLayer.current.bringToBack();
                } else {
                    mapContext.map.removeLayer(shadowLayer.current);
                }
            }

            if (shapeVisible !== undefined && data && data.geoJSON && data.geoJSON.properties && shapeVisible !== data.geoJSON.properties.visible) {
                let attributes = null;
                if (data.type === MarksStyling.TYPE_BUFFER) {
                    attributes = {
                        bufferRadiusMeasurementUnits: bufferRadiusMeasurementUnits,
                        bufferRadius: bufferRadius
                    };
                }

                onMarkChange(id, union, !!shapeVisible, attributes);
            }
        }
    }, [shapeVisible]);


    useEffect(() => {
        if (mapContext.isInitialized && initComplete) {
            let newPathOptions = MarksStyling.applyCustomColor(drawingOptions.pathOptions, customColor);
            let newHintlineStyle = MarksStyling.applyCustomColor(drawingOptions.hintlineStyle, customColor);
            let newTemplineStyle = MarksStyling.applyCustomColor(drawingOptions.templineStyle, customColor);
            if (data.type !== 'Marker') {
                layer.setStyle(newPathOptions);
            } else {
                let newMarkerStylesIcon = L.divIcon({
                    className: 'leaflet-loc-marker',
                    html: MarksStyling.MARKER_SVG.replace('{%%CUSTOM_COLOR%%}', customColor),
                    iconSize: [32, 32],
                    iconAnchor: [16, 32],
                    popupAnchor: [0, -36],
                });
                layer.setIcon(newMarkerStylesIcon);
            }

            if (data.type === MarksStyling.TYPE_BUFFER) {
                if (shadowLayer.current) {
                    shadowLayer.current.setStyle(newPathOptions);
                }
            }

            setDrawingOptions({...drawingOptions, pathOptions: newPathOptions, hintlineStyle: newHintlineStyle, templineStyle: newTemplineStyle});
        }
    }, [customColor]);


    useEffect(() => {
        let newTitle = getTitleFromData();
        if (newTitle !== title) {
            setTitle(newTitle);
        }

        let newShapeVisible = getShapeVisibleFromData(data);
        if (newShapeVisible !== shapeVisible) {
            setShapeVisible(newShapeVisible);
        }

        if (data.geoJSON && data.geoJSON.properties && data.geoJSON.properties.bufferRadius !== bufferRadius) {
            setBufferRadius(data.geoJSON.properties.bufferRadius);

            setBufferRadiusMeasurementUnits(data.geoJSON.properties.bufferRadiusMeasurementUnits ? data.geoJSON.properties.bufferRadiusMeasurementUnits : 'm');
        }
    }, [data]);


    useEffect(() => {
        let newShapeVisible = getShapeVisibleFromData(data);
        if (newShapeVisible !== shapeVisible) {
            setShapeVisible(newShapeVisible);
        }
    }, [visible]);


    useEffect(() => {
        if (!mapContext.isInitialized || !initComplete) {return;}

        if (active) {
            enable();
        } else {
            disable();
        }
    }, [active]);


    useEffect(() => {
        mapContext.map.getPane(mapPaneName).style.zIndex = 500 + sortOrder;
    }, [sortOrder]);


    useEffect(() => {
        updatePopup();
    }, [title, popupTitle]);


    const selfDelete = () => {
        mapContext.map.pm.disableDraw();
        mapContext.map.off('pm:create');

        if (layerRef.current) {mapContext.map.removeLayer(layerRef.current);}
        if (shadowLayer.current) {mapContext.map.removeLayer(shadowLayer.current);}
        if (popupRef.current) {popupRef.current.remove();}

        onActiveChangeRef.current = null;

        onSelfDeleteRef.current(id);

        projectsRequests.deleteShape(projectId, id).finally((response) => {
            onSelfDeleteRef.current(id);
        });
    }


    const _getDryShape = (currentLayer) => {
        if (!layer) {return {
            geoJSON: null,
            layerData: null,
            shadowGeoJSON: null
        };}
        let geoJSON, layerData, shadowGeoJSON = null;

        if (typeof layer.getRadius === 'function') {
            geoJSON = MapHelper.circleToPolygon(currentLayer);
            layerData = {
                center: currentLayer.getLatLng(),
                radius: currentLayer.getRadius()
            };

            geoJSON.properties['radius'] = currentLayer.getRadius();
        } else if (data.type === MarksStyling.TYPE_POINT) {
            geoJSON = currentLayer.toGeoJSON();
            layerData = L.latLngBounds(currentLayer.getLatLng(), currentLayer.getLatLng());
        } else if (data.type === MarksStyling.TYPE_POLYLINE) {
            geoJSON = currentLayer.toGeoJSON();
            layerData = currentLayer.getBounds();
        } else if (data.type === MarksStyling.TYPE_BUFFER) {
            if (shadowLayer.current) {
                shadowGeoJSON = shadowLayer.current.toGeoJSON();
                layerData = shadowLayer.current.getBounds();
            } else {
                layerData = currentLayer.getBounds();
            }

            geoJSON = currentLayer.toGeoJSON();
        } else {
            geoJSON = currentLayer.toGeoJSON();
            layerData = currentLayer.getBounds();
        }

        if (geoJSON.type === 'FeatureCollection' && (geoJSON.features.length === 1)) {
            geoJSON = geoJSON.features[0];
        }

        geoJSON = JSON.parse(JSON.stringify(geoJSON));
        if (!geoJSON.properties) {geoJSON.properties = {};}
        if (customColor) {geoJSON.properties['color'] = customColor;}
        if (notes) {geoJSON.properties['notes'] = notes;}
        if (title) {geoJSON.properties['title'] = title;}
        geoJSON.properties['visible'] = shapeVisible;
        geoJSON.properties['id'] = id;
        geoJSON.properties['popupTitle'] = popupTitle;

        if (data.type === MarksStyling.TYPE_BUFFER) {
            geoJSON.properties['bufferRadius'] = bufferRadius;
            geoJSON.properties['bufferRadiusMeasurementUnits'] = bufferRadiusMeasurementUnits;
        }

        return {
            geoJSON: geoJSON,
            layerData: layerData,
            shadowGeoJSON: shadowGeoJSON
        }
    }


    const initSave = (noChecks = false) => {
        let dryShapeData = _getDryShape(layer);

        if (active || !_.isEqual(data.geoJSON, dryShapeData.geoJSON) || noChecks) {
            let geoJSONCombined = dryShapeData.geoJSON;
            if (data.type === MarksStyling.TYPE_BUFFER) {
                geoJSONCombined = {
                    main: dryShapeData.geoJSON,
                    shadow: dryShapeData.shadowGeoJSON
                }
            }
            onActiveChangeRef.current(id, false, geoJSONCombined, dryShapeData.layerData);
        }
    }


    const onInitRefresh = () => {
        disable();

        let attributes = null;
        if (data.type === MarksStyling.TYPE_BUFFER) {
            attributes = {
                bufferRadiusMeasurementUnits: bufferRadiusMeasurementUnits,
                bufferRadius: bufferRadius
            };
        }

        onMarkChange(id, union, !!shapeVisible, attributes);
    }


    const validateShapeTitle = () => {
        const badTitles = {
            'Marker': ['Marker Pin'],
            'all': ['Shape', 'Shape Polygon', 'Shape Polyline', 'Shape Rectangle', 'Shape Circle']
        };

        return !(badTitles.hasOwnProperty(data.type) && badTitles[data.type].includes(title) || badTitles['all'].includes(title));
    }


    const onSaveToFiles = () => {
        if (!validateShapeTitle()) {
            notifications.notify('Please set a unique name for the shape before saving.', 'error');
            return;
        }

        shapesRequests.create({data: data.geoJSON, name: title}).then((response) => {
            notifications.notify('Shape saved successfully', 'success');
        });
    }


    const disable = () => {
        if (!onActiveChangeRef.current) {return;}

        if (!initComplete) {
            selfDelete();
        } else {
            layer.pm.disable();
            mapContext.map.pm.disableDraw();
        }

        initSave();
    }


    const enable = () => {
        if (!layer) {return;}

        if (canEdit) {
            layer.pm.enable({
                allowSelfIntersection: false,
            });
        }

        if (layer.hasOwnProperty('bringToFront')) {
            layer.bringToFront();
        }

        layer.on('pm:edit', onLayerEdit);

        mapContext.map.once('click', (e) => {
            if (e.sourceTarget === layer) {return;}

            if (typeof onActiveChangeRef.current !== 'function') {return;}

            onActiveChangeRef.current(id, false)
        });
    }


    const onLayerEdit = (e) => {
        let dryShapeData = _getDryShape(layer);

        if (data.type === MarksStyling.TYPE_BUFFER) {
            if (dryShapeData.geoJSON.properties.bufferRadius !== bufferRadiusRef.current) {
                dryShapeData.geoJSON.properties.bufferRadius = bufferRadiusRef.current;
            }
            if (dryShapeData.geoJSON.properties.bufferRadiusMeasurementUnits !== bufferRadiusMeasurementUnitsRef.current) {
                dryShapeData.geoJSON.properties.bufferRadiusMeasurementUnits = bufferRadiusMeasurementUnitsRef.current;
            }
            createBufferShadowLayer(dryShapeData.geoJSON);
            dryShapeData = _getDryShape(layer);
        }
        let geoJSONCombined = dryShapeData.geoJSON;
        if (data.type === MarksStyling.TYPE_BUFFER) {
            dryShapeData.geoJSON.properties.bufferRadius = bufferRadiusRef.current;
            dryShapeData.geoJSON.properties.bufferRadiusMeasurementUnits = bufferRadiusMeasurementUnitsRef.current;

            geoJSONCombined = {
                main: dryShapeData.geoJSON,
                shadow: dryShapeData.shadowGeoJSON
            }
        }

        onActiveChangeRef.current(id, true, geoJSONCombined, dryShapeData.layerData, true);

        updatePopup();
    }


    const onTitleChange = (title) => {
        setTitle(title);

        initSave();
    }


    const updatePopup = () => {
        if (!layer) {return;}

        let popupLatLng;
        if (layer.getLatLng) {
            popupLatLng = layer.getLatLng(); // Marker
        } else if (layer.getBounds) {
            popupLatLng = layer.getBounds().getCenter(); // Polygon, Rectangle, Polyline, Buffer
        }

        if (title && popupTitle) {
            const container = document.createElement('div');
            container.classList.add('leaflet-loc-popup-content-wrapper');
            const root = createRoot(container);
            root.render(<>{title}</>);

            popupRef.current.setLatLng(popupLatLng).setContent(container).openOn(mapContext.map);
        } else {
            popupRef.current.remove();
        }
    }


    const onNotesChange = (notes) => {
        setNotes(notes);

        if (data && layer) {
            layer.on('click', onLayerClick);
        }

        initSave();
    }


    const init = () => {
        mapContext.map.createPane(mapPaneName);
        mapContext.map.getPane(mapPaneName).style.zIndex = sortOrder;

        if (data.layerData) {
            loadShape();
            setDrawingNewShape(false);
        } else {
            if (active && data.type) {
                let customDrawingOptions = {...drawingOptions};
                if (!customColor) {
                    const newCustomColor = MarksStyling.generateRandomColor();
                    setCustomColor(newCustomColor);
                    customDrawingOptions = MarksStyling.applyCustomColor(customDrawingOptions, newCustomColor);
                }

                let drawingType = data.type;
                if (data.type === MarksStyling.TYPE_BUFFER) {
                    drawingType = MarksStyling.TYPE_POLYLINE;
                }

                mapContext.map.pm.enableDraw(drawingType, customDrawingOptions);
                setDrawingNewShape(true);
            }
            mapContext.map.once('pm:create', (e) => {
                if (drawingNewShape) {
                    setDrawingNewShape(false);
                }
                e.layer.on('click', (ev) => {
                    if (!mapContext.map.pm.globalDrawModeEnabled()) {
                        L.DomEvent.preventDefault(ev);
                        L.DomEvent.stopPropagation(ev);

                        onActiveChangeRef.current(id, true);
                    }
                });

                if (data.type === MarksStyling.TYPE_POLYLINE) {
                    let bufferShape = getBufferShape(e.layer.toGeoJSON(), bufferRadius);
                    shadowLayer.current = L.geoJSON(bufferShape.geometry, {style: drawingOptions.pathOptions});
                    shadowLayer.current.addTo(mapContext.map);
                    shadowLayer.current.bringToBack();
                }

                if (data.type === MarksStyling.TYPE_BUFFER) {
                    createBufferShadowLayer(e.layer.toGeoJSON());
                }

                setLayer(e.layer);
                setInitComplete(true);

                let dryShapeData = _getDryShape(e.layer);
                if (onActiveChangeRef.current) {
                    let geoJSONCombined = dryShapeData.geoJSON;
                    if (data.type === MarksStyling.TYPE_BUFFER) {
                        geoJSONCombined = {
                            main: dryShapeData.geoJSON,
                            shadow: dryShapeData.shadowGeoJSON
                        }
                    }
                    onActiveChangeRef.current(id, false, geoJSONCombined, dryShapeData.layerData);
                }
            });
        }
    }


    const createBufferShadowLayer = (layerGeoJSON) => {
        if (shadowLayer.current) {
            mapContext.map.removeLayer(shadowLayer.current);
        }

        let bufferRadiusMeters = parseFloat(bufferRadiusRef.current);
        if (bufferRadiusMeasurementUnits === 'km') {bufferRadiusMeters *= 1000;}
        if (bufferRadiusMeasurementUnits === 'mi') {bufferRadiusMeters *= 1609.34;}

        const bufferShape = getBufferShape(layerGeoJSON, bufferRadiusMeters),
            bufferCoordsPrepared = bufferShape.geometry.coordinates[0].map((coord) => {
                return [coord[1], coord[0]];
            });
        shadowLayer.current = L.polygon(bufferCoordsPrepared, {...drawingOptions.pathOptions});

        shadowLayer.current.addTo(mapContext.map);

        shadowLayer.current.on('click', (e) => {
            if (!mapContext.map.pm.globalDrawModeEnabled()) {
                L.DomEvent.preventDefault(e);
                L.DomEvent.stopPropagation(e);

                onActiveChangeRef.current(id, true);
            }
        });
        shadowLayer.current.bringToBack();
    }


    const loadShape = () => {
        let layer;
        if (data.geoJSON && data.geoJSON.type === 'FeatureCollection' && (data.geoJSON.features.length === 1)) {
            data.geoJSON = data.geoJSON.features[0];
        }

        if (data.type === MarksStyling.TYPE_CIRCLE) {
            layer = L.circle(data.layerData.center, {...drawingOptions.pathOptions, radius: data.layerData.radius});
        } else if (data.type === MarksStyling.TYPE_RECTANGLE) {
            let coordinates = data.geoJSON.geometry.coordinates[0];
            const bounds = L.latLngBounds([coordinates[0][1], coordinates[0][0]], [coordinates[2][1], coordinates[2][0]]);

            layer = L.rectangle(bounds, drawingOptions.pathOptions);
        } else if (data.type === MarksStyling.TYPE_POLYGON) {
            layer = L.geoJSON(data.geoJSON, drawingOptions.pathOptions);
        } else if (data.type === MarksStyling.TYPE_BUFFER) {
            layer = L.geoJSON(data.geoJSON, drawingOptions.pathOptions);

            // using turf.buffer to create a buffer around the shape
            createBufferShadowLayer(data.geoJSON);
        } else if (data.type === MarksStyling.TYPE_POINT) {
            let newMarkerStylesIcon = L.divIcon({
                className: 'leaflet-loc-marker',
                html: MarksStyling.MARKER_SVG.replace('{%%CUSTOM_COLOR%%}', customColor),
                iconSize: [32, 32],
                iconAnchor: [16, 32],
                popupAnchor: [0, -36],
            });
            layer = L.marker(data.geoJSON.geometry.coordinates.toReversed(), {icon: newMarkerStylesIcon});
        } else if (data.type === MarksStyling.TYPE_POLYLINE) {
            layer = L.geoJSON(data.geoJSON, drawingOptions.pathOptions);
        } else {
            return false;
        }

        if (canEdit) {
            layer.pm.enable(drawingOptions);
        }
        if (shapeVisible) {
            layer.addTo(mapContext.map);
        }


        layer.on('click', onLayerClick);

        setLayer(layer);

        setInitComplete(true);
    }


    const onLayerClick = useCallback((e) => {
        L.DomEvent.preventDefault(e);
        L.DomEvent.stopPropagation(e);

        if (!mapContext.map.pm.globalDrawModeEnabled() && !data.active) {
            L.DomEvent.preventDefault(e);
            L.DomEvent.stopPropagation(e);

            onActiveChangeRef.current(id, true);
        }
    }, [active]);


    const onPanelMinimizeChange = (minimized) => {
        setInfoPanelMinimized(minimized);
    }


    const onLayerToTop = () => {
        if (!layer) {return;}

        if (data.type === MarksStyling.TYPE_BUFFER && shadowLayer.current) {
            shadowLayer.current.bringToFront();
        }
        layer.bringToFront();

        onBringToFront(id);
    }


    const onLayerToBottom = () => {
        if (!layer) {return;}

        layer.bringToBack();
        if (data.type === MarksStyling.TYPE_BUFFER && shadowLayer.current) {
            shadowLayer.current.bringToBack();
        }

        onBringToBack(id);
    }


    const onToggleVisible = (newVisible) => {
        setShapeVisible(newVisible);
    }


    const onTogglePopupTitle = () => {
        setPopupTitle(!popupTitle);
    }


    const getBufferShape = (shape, padding) => {
        return turf.buffer(shape, padding, {units: 'meters'});
    }


    return (<>
        {active && !drawingNewShape ? (
            <MarksInfoPanel
                id={id}
                data={data}
                title={title}
                properties={properties}
                canEdit={canEdit}

                onTitleChange={onTitleChange}
                setCustomColor={setCustomColor}
                selfDelete={selfDelete}
                onNotesChange={onNotesChange}

                onInitRefresh={onInitRefresh}
                onSaveToFiles={onSaveToFiles}

                onLayerToTop={onLayerToTop}
                onLayerToBottom={onLayerToBottom}
                onToggleVisible={onToggleVisible}
                onTogglePopupTitle={onTogglePopupTitle}
                onConvertShape={onConvertShape}

                bufferRadiusChanged={(value) => {
                    setBufferRadius(value.radius);
                    setBufferRadiusMeasurementUnits(value.measurementUnits);
                }}

                panelMinimize={infoPanelMinimized}
                onPanelMinimizeChange={onPanelMinimizeChange}
            />
        ) : null}
    </>);
}